import React from 'react';
import { View, StyleSheet, Pressable, Text } from 'react-native';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Feather from '@expo/vector-icons/Feather';
import { DedupeContent, TidyDecisions } from '../DedupeContent';
import {
  selectCertainDuplicateGroups,
  selectMergeCandidates,
  selectUnsortedItems,
  selectTidyCount,
} from '../../../redux/pantrySelectors';
import { useTidyActions } from '../useTidyActions';
import { Paprika, PaprikaSpacing, PaprikaTypography } from '../../../styles/theme';

interface DedupePanelProps {
  onClose: () => void;
  flash: (message: string) => void;
}

export function DedupePanel({ onClose, flash }: DedupePanelProps) {
  const certain = useSelector(selectCertainDuplicateGroups);
  const candidates = useSelector(selectMergeCandidates);
  const unsorted = useSelector(selectUnsortedItems);
  const pending = useSelector(selectTidyCount);
  const { t } = useTranslation();
  const applyDecisions = useTidyActions(flash);

  const handleConfirm = (decisions: TidyDecisions) => {
    applyDecisions(decisions);
    onClose();
  };

  return (
    <View style={styles.root}>
      <View style={styles.header}>
        <View>
          <Text style={styles.title}>{t('pantry.tidy.title')}</Text>
          <Text style={styles.subtitle}>
            {t(`pantry.tidy.panelSub_${pending === 1 ? 'one' : 'other'}`, { count: pending })}
          </Text>
        </View>
        <Pressable onPress={onClose} hitSlop={8} style={styles.closeButton}>
          <Feather name="x" size={20} color={Paprika.textMuted} />
        </Pressable>
      </View>

      <View style={styles.content}>
        <DedupeContent
          certain={certain}
          candidates={candidates}
          unsorted={unsorted}
          onConfirm={handleConfirm}
          onCancel={onClose}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
    backgroundColor: Paprika.background,
    borderLeftWidth: 1,
    borderLeftColor: Paprika.hairline,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    paddingVertical: PaprikaSpacing.md,
    paddingHorizontal: PaprikaSpacing.md,
    borderBottomWidth: 1,
    borderBottomColor: Paprika.hairline,
  },
  title: {
    ...PaprikaTypography.headingMedium,
    color: Paprika.textPrimary,
    marginBottom: 4,
  },
  subtitle: {
    fontFamily: 'Inter-Regular',
    fontSize: 12,
    color: Paprika.textMuted,
  },
  closeButton: {
    padding: 8,
  },
  content: {
    flex: 1,
  },
});
